import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOffers,
  updateOffer,
  createOffer,
  deleteOffer,
  fetchOffer,
} from "../api/offer";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  offer: {},
  totalCount: 0,
  currentCount: 0,
  newestOffersCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 200,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

export const deleteOfferAsync = createAsyncThunk(
  "offers/deleteOfferAsync",
  async (payload) => {
    let response = await deleteOffer(payload);

    // If errors, reject and return response
    if (response.error) return false;

    // If all good, return response.
    return response;
  }
);

export const createOfferAsync = createAsyncThunk(
  "offers/createOfferAsync",
  async (payload, { rejectWithValue }) => {
    try {
      let response = await createOffer(payload);
      // If response has error structure, reject
      if (response?.error || response?.errors) {
        return rejectWithValue(response);
      }
      // If all good, return response.
      return response;
    } catch (error) {
      // Handle rejected promise from createOffer
      if (error.response) {
        return rejectWithValue(error);
      }
      // Fallback error
      return rejectWithValue({
        response: {
          status: 500,
          data: { error: error.message || "Unknown error" },
        },
      });
    }
  }
);

export const updateOfferAsync = createAsyncThunk(
  "offers/updateOfferAsync",
  async (payload, { rejectWithValue }) => {
    try {
      let response = await updateOffer(payload);
      // If response has error structure, reject
      if (response?.error || response?.errors) {
        return rejectWithValue(response);
      }
      // If all good, return response.
      return response;
    } catch (error) {
      // Handle rejected promise from updateOffer
      if (error.response) {
        return rejectWithValue(error);
      }
      // Fallback error
      return rejectWithValue({
        response: {
          status: 500,
          data: { error: error.message || "Unknown error" },
        },
      });
    }
  }
);

export const getOffersAsync = createAsyncThunk(
  "offers/getOffersAsync",
  async (controller = initialController, { rejectWithValue }) => {
    let response = await fetchOffers(controller);
    // If errors, reject and return response
    if (response.response?.status?.match(/404|500|401/))
      return rejectWithValue(true);

    // If all good, return response.
    return response;
  }
);

export const getOfferAsync = createAsyncThunk(
  "offers/getOfferAsync",
  async (id, { rejectWithValue }) => {
    try {
      let response = await fetchOffer(id);

      if (response.error || response?.response?.status) {
        return rejectWithValue(response);
      }

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const offerSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOffersAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getOffersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestOffersCount = action.payload.newestOffersCount;
      })
      .addCase(getOffersAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to fetch Offers....is controller missing?",
        ];
      })
      .addCase(updateOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.offer = action.payload;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateOfferAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to update Offer."];
      })
      .addCase(createOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createOfferAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to create Offer."];
      })
      .addCase(getOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.offer = action.payload;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(getOfferAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to fetch Offer."];
      })
      .addCase(deleteOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(deleteOfferAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to delete Offer."];
      });
  },
});

export default offerSlice.reducer;

